import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, X, Loader2, ImagePlus, GripVertical } from 'lucide-react';
import imageCompression from 'browser-image-compression';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 10 * 1024 * 1024;
const MAX_PHOTOS = 20;
const ALLOWED = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

// Compress if the file is > 800 KB or > 2000 px on the long edge.
const COMPRESS_OPTIONS = {
  maxSizeMB: 0.8,
  maxWidthOrHeight: 2000,
  useWebWorker: true,
  fileType: 'image/jpeg', // downscale iPhone HEIC/PNG → JPEG for smaller payloads
  initialQuality: 0.85,
};

/**
 * Multi-photo uploader for the Sell form.
 *   props.value    — string[] of already-uploaded photo URLs (absolute or /api/uploads/...)
 *   props.onChange — (nextUrls: string[]) => void
 */
export default function PhotoUploader({ value = [], onChange }) {
  const [uploading, setUploading] = React.useState(0);
  const [dragging, setDragging] = React.useState(false);
  const inputRef = React.useRef(null);
  const dragFromRef = React.useRef(null);

  const absolutise = (url) => (url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`);

  const uploadOne = async (file) => {
    if (!ALLOWED.includes(file.type)) {
      toast.error(`${file.name}: unsupported format`);
      return null;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`${file.name}: file too large (max 10 MB before compression)`);
      return null;
    }

    // Client-side downscale for iPhone / DSLR photos (skips GIFs which are usually animations).
    let toUploadFile = file;
    if (file.type !== 'image/gif' && file.size > 800 * 1024) {
      try {
        const compressed = await imageCompression(file, COMPRESS_OPTIONS);
        // browser-image-compression returns a Blob; give it a filename
        toUploadFile = new File([compressed], (file.name.replace(/\.\w+$/, '') || 'photo') + '.jpg', {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });
      } catch {
        // fall back to original file if compression fails
        toUploadFile = file;
      }
    }

    const fd = new FormData();
    fd.append('file', toUploadFile);
    try {
      const { data } = await axios.post(`${API}/uploads/photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return absolutise(data.url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${file.name}: upload failed`);
      return null;
    }
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const room = MAX_PHOTOS - value.length;
    if (room <= 0) return toast.error(`Max ${MAX_PHOTOS} photos`);
    const toUpload = files.slice(0, room);
    if (files.length > room) toast.info(`Only uploading first ${room} photo(s) — limit is ${MAX_PHOTOS}`);

    setUploading((n) => n + toUpload.length);
    const results = await Promise.all(toUpload.map(uploadOne));
    setUploading((n) => n - toUpload.length);
    const good = results.filter(Boolean);
    if (good.length) {
      onChange([...value, ...good]);
      toast.success(`${good.length} photo${good.length > 1 ? 's' : ''} uploaded`);
    }
  };

  const remove = (i) => {
    const next = value.slice();
    next.splice(i, 1);
    onChange(next);
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    handleFiles(e.dataTransfer.files);
  };
  const onDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);

  const onThumbDragStart = (i) => (e) => {
    dragFromRef.current = i;
    e.dataTransfer.effectAllowed = 'move';
  };
  const onThumbDragOver = (i) => (e) => { e.preventDefault(); };
  const onThumbDrop = (i) => (e) => {
    e.preventDefault();
    const from = dragFromRef.current;
    if (from == null || from === i) return;
    const next = value.slice();
    const [moved] = next.splice(from, 1);
    next.splice(i, 0, moved);
    dragFromRef.current = null;
    onChange(next);
  };

  return (
    <div className="space-y-3" data-testid="photo-uploader">
      {/* Drop zone */}
      <div
        onClick={() => inputRef.current?.click()}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        role="button"
        tabIndex={0}
        className={`hairline p-6 text-center cursor-pointer transition-colors ${
          dragging ? 'border-[#ff2a2a] bg-[#ff2a2a]/5' : 'hover:border-white/25 bg-[#0a0a0a]'
        }`}
        data-testid="photo-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="photo-input"
        />
        {uploading > 0 ? (
          <div className="inline-flex items-center gap-2 text-sm text-zinc-300">
            <Loader2 className="w-4 h-4 animate-spin text-[#ff2a2a]" /> Uploading {uploading} photo{uploading > 1 ? 's' : ''}…
          </div>
        ) : (
          <>
            <Upload className="w-6 h-6 text-zinc-500 mx-auto" />
            <div className="mt-2 text-sm text-zinc-200 font-semibold">
              Drop photos here — or click to pick from your device
            </div>
            <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              JPG · PNG · WEBP · GIF · Max 10 MB each · Up to {MAX_PHOTOS} photos · Auto-compressed on upload
            </div>
          </>
        )}
      </div>

      {/* Thumbnails */}
      {value.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="photo-thumbs">
          {value.map((url, i) => (
            <div
              key={url + i}
              draggable
              onDragStart={onThumbDragStart(i)}
              onDragOver={onThumbDragOver(i)}
              onDrop={onThumbDrop(i)}
              className="relative aspect-square hairline group overflow-hidden bg-[#0a0a0a]"
              data-testid={`photo-thumb-${i}`}
            >
              <img src={url} alt={`upload-${i}`} className="w-full h-full object-cover" />
              {i === 0 && (
                <div className="absolute top-1 left-1 am-pill !py-0.5 !px-1.5 !text-[9px] bg-black/70 backdrop-blur-md">
                  Cover
                </div>
              )}
              <div className="absolute top-1 right-1 flex gap-1">
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="w-6 h-6 grid place-items-center bg-black/70 hover:bg-[#ff2a2a] text-white transition-colors"
                  data-testid={`photo-remove-${i}`}
                  title="Remove"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="absolute bottom-1 left-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-5 h-5 grid place-items-center bg-black/70 text-white" title="Drag to reorder">
                  <GripVertical className="w-3 h-3" />
                </div>
              </div>
            </div>
          ))}
          {value.length < MAX_PHOTOS && (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="aspect-square hairline hover:border-[#ff2a2a] hover:text-[#ff2a2a] text-zinc-500 flex flex-col items-center justify-center gap-1 transition-colors bg-[#0a0a0a]"
              data-testid="photo-add-more"
            >
              <ImagePlus className="w-5 h-5" />
              <span className="text-[10px] font-mono uppercase tracking-widest">Add more</span>
            </button>
          )}
        </div>
      )}

      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">
        First photo is the cover · Drag thumbnails to reorder
      </div>
    </div>
  );
}
